import { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Figure, Corner, SectionHead } from "./Primitives";
import { Illu } from "./Illustrations";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, Package } from "lucide-react";

const empty = {
  variant: "",
  quantity: 1,
  name: "",
  email: "",
  phone: "",
  street: "",
  zip: "",
  city: "",
  country: "Österreich",
  message: "",
  consent: false,
  honeypot: "",
};

export const Shop = ({ texts, products }) => {
  const [active, setActive] = useState(null);
  const [form, setForm] = useState(empty);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const open = (p) => {
    setActive(p);
    setForm({ ...empty, variant: (p.variants || "").split(",")[0]?.trim() || "" });
    setState("idle");
    setError("");
  };

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setState("sending");
    try {
      await api.post("/order-inquiries", {
        ...form,
        quantity: Number(form.quantity) || 1,
        product_id: active.id,
      });
      setState("success");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
      setState("idle");
    }
  };

  return (
    <section id="artikel" data-testid="shop-section" className="relative border-b border-paper-line ap-section">
      <div className="ap-container">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead stamp="AP · ARTIKEL" title={texts.shop_h2} intro={texts.shop_intro} />
          <div className="ap-illu hidden h-24 w-24 shrink-0 lg:block" aria-hidden="true">
            <Illu name={texts.illu_shop} fallback="order" className="h-full w-full" />
          </div>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => (
            <article key={p.id} data-testid={`product-card-${i}`} className="ap-card group flex flex-col">
              <Corner />
              <Figure
                src={p.image}
                mediaId={p.image_media_id}
                alt={`${p.title} – ArtPart Artikel`}
                ratio="aspect-[4/3]"
              />
              <div className="flex flex-1 flex-col gap-2 p-5">
                <h3 className="font-display text-lg font-bold uppercase tracking-wide text-ink">{p.title}</h3>
                <p className="ap-body text-charcoal">{p.description}</p>
                <p className="mt-1 font-display text-xl font-black text-ink">{p.price}</p>
                {p.variants ? <p className="ap-stamp">Varianten: {p.variants}</p> : null}
                <p className="ap-stamp">Status: {p.availability}</p>
                <p className="ap-body mt-1 text-xs text-charcoal">{p.shipping_note}</p>
                <button type="button" onClick={() => open(p)} data-testid={`product-cta-${i}`} className="ap-btn-solid mt-4 w-full">
                  <Package size={15} /> Bestellanfrage senden
                </button>
              </div>
            </article>
          ))}
        </div>

        <p className="ap-body mt-8 border-l-2 border-signal bg-paper-surface/60 p-5 text-charcoal" data-testid="shop-notice">
          {texts.shop_notice}{" "}
          <Link to="/versand" className="underline hover:text-signal">Versand & Bezahlung</Link>
        </p>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none border-paper-line bg-paper sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold uppercase tracking-wide text-ink">
              Bestellanfrage
            </DialogTitle>
          </DialogHeader>

          {state === "success" ? (
            <div className="py-6 text-center" data-testid="order-success">
              <CheckCircle2 size={38} className="mx-auto text-signal" />
              <p className="ap-h3 mt-4 text-ink">Anfrage übermittelt.</p>
              <p className="ap-body mt-3 text-charcoal">{texts.shop_notice}</p>
              <button type="button" onClick={() => setActive(null)} className="ap-btn-solid mt-6 w-full" data-testid="order-close">
                Schließen
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="order-form" className="space-y-4">
              <p className="border border-paper-line bg-paper-surface p-3 ap-stamp" data-testid="order-product">
                Artikel: {active?.title} · {active?.price}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {active?.variants ? (
                  <div>
                    <label className="ap-label" htmlFor="of-variant">Variante</label>
                    <select id="of-variant" value={form.variant} onChange={set("variant")} className="ap-field" data-testid="order-variant">
                      {active.variants.split(",").map((v) => (
                        <option key={v.trim()} value={v.trim()}>{v.trim()}</option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div>
                  <label className="ap-label" htmlFor="of-qty">Stückzahl</label>
                  <input id="of-qty" type="number" min={1} max={20} value={form.quantity} onChange={set("quantity")} className="ap-field" data-testid="order-quantity" />
                </div>
                <div>
                  <label className="ap-label" htmlFor="of-name">Vor- und Nachname *</label>
                  <input id="of-name" required value={form.name} onChange={set("name")} className="ap-field" data-testid="order-name" />
                </div>
                <div>
                  <label className="ap-label" htmlFor="of-email">E-Mail *</label>
                  <input id="of-email" type="email" required value={form.email} onChange={set("email")} className="ap-field" data-testid="order-email" />
                </div>
                <div>
                  <label className="ap-label" htmlFor="of-phone">Telefon (optional)</label>
                  <input id="of-phone" value={form.phone} onChange={set("phone")} className="ap-field" data-testid="order-phone" />
                </div>
                <div>
                  <label className="ap-label" htmlFor="of-street">Straße und Hausnummer *</label>
                  <input id="of-street" required value={form.street} onChange={set("street")} className="ap-field" data-testid="order-street" />
                </div>
                <div>
                  <label className="ap-label" htmlFor="of-zip">PLZ *</label>
                  <input id="of-zip" required value={form.zip} onChange={set("zip")} className="ap-field" data-testid="order-zip" />
                </div>
                <div>
                  <label className="ap-label" htmlFor="of-city">Ort *</label>
                  <input id="of-city" required value={form.city} onChange={set("city")} className="ap-field" data-testid="order-city" />
                </div>
                <div className="sm:col-span-2">
                  <label className="ap-label" htmlFor="of-country">Land</label>
                  <input id="of-country" value={form.country} onChange={set("country")} className="ap-field" data-testid="order-country" />
                </div>
                <div className="sm:col-span-2">
                  <label className="ap-label" htmlFor="of-msg">Nachricht (optional)</label>
                  <textarea id="of-msg" rows={3} value={form.message} onChange={set("message")} className="ap-field" data-testid="order-message" />
                </div>
              </div>

              <input type="text" tabIndex={-1} value={form.honeypot} onChange={set("honeypot")} aria-hidden="true" className="absolute h-0 w-0 opacity-0" />

              <label className="flex cursor-pointer items-start gap-3 text-sm text-charcoal">
                <input type="checkbox" required checked={form.consent} onChange={set("consent")} className="mt-1 h-5 w-5 shrink-0 accent-signal" data-testid="order-consent" />
                <span>
                  Ich stimme der Verarbeitung meiner Angaben zur Bearbeitung der Bestellanfrage zu.{" "}
                  <Link to="/datenschutz" className="underline hover:text-signal">Datenschutz</Link>
                </span>
              </label>

              <p className="ap-body border-l-2 border-signal pl-3 text-xs text-charcoal">{texts.shop_notice}</p>

              {error ? (
                <p role="alert" data-testid="order-error" className="border border-destructive bg-destructive/5 p-3 text-sm text-destructive">
                  {error}
                </p>
              ) : null}

              <button type="submit" disabled={state === "sending"} className="ap-btn-solid w-full disabled:opacity-60" data-testid="order-submit">
                {state === "sending" ? <Loader2 size={16} className="animate-spin" /> : null} Unverbindlich anfragen
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
